import React, { useEffect, useRef, Suspense, lazy } from 'react';
import '@/App.css';
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Home from './pages/Home';
// iter86 · Code-split every non-home page. Home stays eager to keep
// LCP fast on the landing route. Admin + section + legal + lab pages
// get their own chunks so a fresh homepage load doesn't pay for admin
// bundles (recharts, editor tooling) it will never render for public
// visitors. Reduces main bundle from ~8MB → ~2.5MB decompressed.
const SectionPage      = lazy(() => import('./pages/SectionPage'));
const AdminLogin       = lazy(() => import('./pages/AdminLogin'));
const AdminShell       = lazy(() => import('./pages/AdminShell'));
const AdminDashboard   = lazy(() => import('./pages/AdminDashboard'));
const AdminSectionEditor = lazy(() => import('./pages/AdminSectionEditor'));
const AdminContacts    = lazy(() => import('./pages/AdminContacts'));
const AdminMediaLibrary = lazy(() => import('./pages/AdminMediaLibrary'));
const AdminStats       = lazy(() => import('./pages/AdminStats'));
const AdminManifestoSeeds = lazy(() => import('./pages/AdminManifestoSeeds'));
const AdminAphorismSeeds = lazy(() => import('./pages/AdminAphorismSeeds'));
const AdminPress       = lazy(() => import('./pages/AdminPress'));
const AdminPortfolioItems = lazy(() => import('./pages/AdminPortfolioItems'));
const AdminPrintAssets = lazy(() => import('./pages/AdminPrintAssets'));
const LegalPage        = lazy(() => import('./pages/LegalPage'));
const ContactsPage     = lazy(() => import('./pages/ContactsPage'));
const NotFoundPage     = lazy(() => import('./pages/NotFoundPage'));
const VisionHeroLab    = lazy(() => import('./pages/lab/VisionHeroLab'));
const VisionMateriaLab = lazy(() => import('./pages/lab/VisionMateriaLab'));
import CookieBanner from './components/CookieBanner/CookieBanner';
import GA4Loader from './components/Analytics/GA4Loader';
import { ContactsProvider } from './contexts/ContactsContext';
import { api } from './lib/api';
import { getLangFromPath } from './lib/lang';

function sessionId() {
    try {
        let s = sessionStorage.getItem('araia_pv_sid');
        if (!s) {
            s = `pv-${Math.random().toString(36).slice(2)}-${Date.now()}`;
            sessionStorage.setItem('araia_pv_sid', s);
        }
        return s;
    } catch (e) {
        return 'anon';
    }
}

/** Fires a POST /api/analytics/pageview on every route change. Silent. */
function PageviewBeacon() {
    const loc = useLocation();
    const last = useRef(null);
    useEffect(() => {
        // Ignore admin traffic
        if (loc.pathname.startsWith('/admin')) return;
        if (last.current === loc.pathname) return;
        last.current = loc.pathname;
        const lang = getLangFromPath(loc.pathname);
        api.pageview(loc.pathname, sessionId(), lang);
    }, [loc.pathname]);
    return null;
}

/** iter70 · Global click listener that fires
 *  `POST /api/analytics/track` when the user clicks any element
 *  carrying a `data-analytics="<event>"` attribute (event bubbles up
 *  through parent nodes so inner spans/icons also trigger). Silent —
 *  errors are swallowed, never blocks the click. Excluded on admin
 *  routes.
 */
function ClickAnalyticsBeacon() {
    const loc = useLocation();
    useEffect(() => {
        if (loc.pathname.startsWith('/admin')) return undefined;
        const handler = (e) => {
            const el = e.target?.closest?.('[data-analytics]');
            if (!el) return;
            const event = el.getAttribute('data-analytics');
            if (!event) return;
            const lang = getLangFromPath(loc.pathname);
            api.trackEvent(event, sessionId(), lang, {
                path: loc.pathname,
                href: el.getAttribute('href') || undefined,
            });
        };
        document.addEventListener('click', handler, true);
        return () => document.removeEventListener('click', handler, true);
    }, [loc.pathname]);
    return null;
}

/**
 * Italian is the default (unprefixed) language. Historically some
 * visitors bookmark or type `/it/...` expecting it to resolve — we
 * transparently redirect to the equivalent unprefixed path so those
 * URLs never dead-end on a blank screen.
 */
function ItRedirect() {
    const loc = useLocation();
    const target = loc.pathname.replace(/^\/it(\/|$)/, '/') + loc.search + loc.hash;
    return <Navigate to={target || '/'} replace />;
}

/**
 * iter75 · Instant scroll-to-top on every pathname change.
 *
 * Without this, React Router preserves the previous scroll position
 * when the new route mounts — so if the user scrolled far down and
 * clicked a link, the new page could briefly render its footer
 * inside the viewport before the top content lands.
 *
 * `behavior: 'instant'` (or a plain scrollTo without `smooth`) is
 * required so we don't chain an animation on top of the route
 * transition. We honour `prefers-reduced-motion` implicitly by not
 * animating.
 */
function ScrollToTopOnNav() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pathname]);
    return null;
}

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                    <ContactsProvider>
                        <ScrollToTopOnNav />
                        <PageviewBeacon />
                        <ClickAnalyticsBeacon />
                    {/* iter86 · Suspense fallback for lazy-loaded routes.
                        Minimal transparent placeholder so a route swap
                        never flashes an ugly loader — the previous route
                        stays painted until the next chunk hydrates. */}
                    <Suspense fallback={null}>
                    <Routes>
                        {/* Public site — both languages */}
                        <Route path="/" element={<Home />} />
                        <Route path="/en" element={<Home />} />

                        {/* /it and /it/* → strip prefix and serve the IT default site */}
                        <Route path="/it" element={<ItRedirect />} />
                        <Route path="/it/*" element={<ItRedirect />} />

                        {/* Admin — login is public entry, everything else is shell-guarded */}
                        <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<AdminShell />}>
                            <Route path="dashboard" element={<AdminDashboard />} />
                            <Route path="sections/:slug" element={<AdminSectionEditor />} />
                            <Route path="contacts" element={<AdminContacts />} />
                            <Route path="media" element={<AdminMediaLibrary />} />
                            <Route path="stats" element={<AdminStats />} />
                            <Route path="manifesto" element={<AdminManifestoSeeds />} />
                            <Route path="aphorism" element={<AdminAphorismSeeds />} />
                            <Route path="portfolio" element={<AdminPortfolioItems />} />
                            <Route path="press" element={<AdminPress />} />
                            <Route path="print-assets" element={<AdminPrintAssets />} />
                        </Route>

                        {/* Lab / experiments — standalone, not linked from public nav */}
                        <Route path="/lab/vision-hero" element={<VisionHeroLab />} />
                        <Route path="/lab/vision-materia" element={<VisionMateriaLab />} />

                        {/* Public content pages — declared LAST so `/admin` is not
                            eaten by the greedy `:world/:page` pattern */}
                        <Route path="/en/legal/:slug" element={<LegalPage />} />
                        <Route path="/legal/:slug" element={<LegalPage />} />
                        {/* iter50 · dedicated contacts page + EN alias */}
                        <Route path="/contatti" element={<ContactsPage />} />
                        <Route path="/en/contacts" element={<ContactsPage />} />
                        <Route path="/en/:world/:page" element={<SectionPage />} />
                        <Route path="/:world/:page" element={<SectionPage />} />
                        {/* iter50 v2 · 1-segment EN URL (/en/foo) — must be
                            declared AFTER the 2-segment EN routes so exact
                            matches win. Renders localised NotFound. */}
                        <Route path="/en/:only" element={<NotFoundPage />} />
                        {/* iter50 · catch-all: any unknown URL renders the
                            localised NotFound instead of a blank #root. */}
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </Suspense>
                    <CookieBanner />
                    <GA4Loader />
                    </ContactsProvider>
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
